import { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useParams, useResolvedPath } from 'react-router-dom';
import { invariant } from '@utils/invariant';
import { getOrderKmBreakdown, getOrderSustainabilityFigures } from '@utils/order-km';
import * as Yup from 'yup';
import { ArrowSquareOutIcon, PencilIcon, UserIcon } from '@phosphor-icons/react';

import { Breadcrumb } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/button/Button';
import { DataField } from '../../../../components/DataField';
import { InputDialog } from '../../../../components/dialog/InputDialog';
import { Dialog } from '../../../../components/dialog/Dialog';
import { PageHeader } from '../../../../components/PageHeader';
import { SimpleSelect, ISimpleSelectItem } from '../../../../components/select/SimpleSelect';
import { UserCombobox, type InternalUser } from '../../../user/components/UserCombobox';
import { Pill } from '../../../../components/pill/Pill';
import { ITabItem, Tabs } from '../../../../components/tabs/Tabs';
import { Tooltip } from '../../../../components/tooltip/Tooltip';
import {
  OrderDocumentType,
  OrderDocumentValidationStatus,
  OrderSelfBillReviewFlag,
  OrderStatus,
  SaleStatus,
  OrderLineStopType,
  EmailType,
} from 'src/types/enums';
import { InternalRole } from '../../../auth/endpoints/MeEndpoint.types';
import { formatDate, sortDate } from '../../../../utils/date';
import { getDisplayError } from '../../../../utils/get-display-error';
import { ErrorState } from '../../../../components/error-state/ErrorState';
import { decimateAndFormatNumber } from '../../../../utils/number';
import { OrderStatusTag } from '../../components/OrderStatusTag';
import { OrderDocumentsBar } from '../../components/OrderDocumentsBar';
import { OrderDocumentValidationBadge } from '../../components/OrderDocumentValidationBadge';
import { OrderSelfBillExclusionAction } from '../../components/OrderSelfBillExclusionAction';
import { OrderSelfBillReviewBanner } from '../../components/OrderSelfBillReviewBanner';
import {
  ORDER_ORIGINS,
  PURCHASE_STATUS_COLOR,
  PURCHASE_STATUS_OPTIONS,
  SALE_STATUS_COLOR,
  SALE_STATUS_OPTIONS,
} from '../../constants';
import { canCreateOrderLine } from '../../utils/order-status';
import { calculateEuroPerKm, calculateLinesTotalExclVat, calculateProfitPercentage } from '../../utils/price';
import { OrderLineSale } from '../orderLineSale/OrderLineSales';
import { OrderLinePurchase } from '../orderPurchase/OrderPurchases';
import { OrderProvider } from './orderContext';
import { OrderStatusDialog } from '../../components/OrderStatusDialog';
import { ContactsAndContentDialog } from '../../../../components/dialog/ContactsAndContentDialog';
import { CheckboxField } from '../../../../components/checkbox/CheckboxField';
import { useMinimalGeneralSettings } from '../../../../contexts/minimal-settings-context';
import { RatingDatafield } from '../../components/RatingDatafield';
import { useAuth } from '../../../../contexts/auth-context';
import { LinkButton } from '../../../../components/button/ButtonLink';
import classNames from '../../../../utils/classnames';
import { ContactsDialog } from '../../../../components/dialog/ContactsDialog';
import { PageHeading } from 'components/PageHeading';
import { DelayedSpinner } from 'components/Spinner';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../../utils/track-event';
import { roleToNumber } from 'src/app/auth/roles.client';
import {
  assignOrder,
  cancelOrder as cancelOrderRequest,
  sendOrderConfirmation as sendOrderConfirmationRequest,
  sendTransportOrder as sendTransportOrderRequest,
  sendUrgentCmrReminder as sendUrgentCmrReminderRequest,
  updateOrder,
  useOrderById,
  type OrderDetail,
} from '../../models';
import { getOrderValidationError } from '../../utils/order-validation';

export type Order = OrderDetail;

function getLineSales(order: Order): OrderLineSale[] {
  let sales: OrderLineSale[] = [];
  for (const line of order.lines) {
    sales.push(...line.sales);
  }
  return sales;
}

function getLinePurchases(order: Order): OrderLinePurchase[] {
  let purchases: OrderLinePurchase[] = [];
  for (const line of order.lines) {
    purchases.push(...line.purchases);
  }
  return purchases;
}

const OrderPage = () => {
  const { me } = useAuth();
  const { orderId } = useParams<{ orderId: string }>();
  invariant(orderId);
  const { settings: minimalSettings } = useMinimalGeneralSettings();
  const { data, error, isLoading, mutate } = useOrderById(orderId);
  const [showManualStatusChangeDialog, setShowManualStatusChangeDialog] = useState(false);

  // Assignment dialog state
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  // Responsible user dialog state
  const [isResponsibleDialogOpen, setIsResponsibleDialogOpen] = useState(false);
  const [selectedResponsibleUser, setSelectedResponsibleUser] = useState<InternalUser | null>(null);
  const [isSavingResponsibleUser, setIsSavingResponsibleUser] = useState(false);

  const order = data;

  const refetchData = useCallback(async () => {
    await mutate();
  }, [mutate]);

  const generalRoute = useResolvedPath('general');
  const linesRoute = useResolvedPath(order?.lines.length === 1 ? `lines/${order.lines[0].id}/contents` : 'lines');
  const complaintsRoute = useResolvedPath('complaints');
  const trackingRoute = useResolvedPath('tracking');
  const chatRoute = useResolvedPath('chat');
  const spotMarketRoute = useResolvedPath('spot-market');
  const auditLogsRoute = useResolvedPath('audit-logs');
  const tabItems = useMemo<ITabItem[]>(() => {
    const res = [
      {
        title: 'Algemeen',
        path: generalRoute.pathname,
      },
      {
        title: `Opdrachten (${order?.lines.length ?? '0'})`,
        path: linesRoute.pathname,
      },
      {
        title: `Klachten (${order?.complaints.length ?? '0'})`,
        path: complaintsRoute.pathname,
      },
      {
        title: 'Tracking',
        path: trackingRoute.pathname,
      },
      {
        title: 'Chat',
        path: chatRoute.pathname,
      },
    ];

    if (me.isInternal) {
      res.push({
        title: 'Spotmarkt',
        path: spotMarketRoute.pathname,
      });
    }

    if (roleToNumber(me.role) >= roleToNumber(InternalRole.InternalAdmin)) {
      res.push({
        title: 'Audit logs',
        path: auditLogsRoute.pathname,
      });
    }

    return res;
  }, [order, me]);

  const purchaseTotal = useMemo(() => {
    return order ? calculateLinesTotalExclVat(getLinePurchases(order)) : 0;
  }, [order]);
  const saleTotal = useMemo(() => {
    return order ? calculateLinesTotalExclVat(getLineSales(order)) : 0;
  }, [order]);

  const missingOrderData = useMemo(() => getOrderValidationError(order), [order]);

  // UserIcon options for assignment dropdown
  const userOptions: ISimpleSelectItem[] = useMemo(() => {
    if (!order?.supplier?.portalUsers) return [];
    return order.supplier.portalUsers
      .filter((u) => u.user.isActive)
      .map((u) => ({
        key: u.user.id,
        name: u.nickname ? u.nickname : u.user.name,
      }));
  }, [order?.supplier?.portalUsers]);

  // Assignment dialog handlers
  const handleOpenAssignDialog = () => {
    setSelectedAssigneeId(order?.assignee?.id ?? null);
    setIsAssignDialogOpen(true);
  };

  const handleAssignOrder = async () => {
    if (!orderId) return;

    setIsAssigning(true);
    try {
      await assignOrder(orderId, selectedAssigneeId);
      trackEvent('order_assigned', {
        order_id: orderId,
        assignee_id: selectedAssigneeId,
      });
      toast.success('Order toegewezen');
      setIsAssignDialogOpen(false);
      await refetchData();
    } catch (err) {
      toast.error(getDisplayError(err));
    } finally {
      setIsAssigning(false);
    }
  };

  // Responsible user dialog handlers
  const handleOpenResponsibleDialog = () => {
    setSelectedResponsibleUser(order?.responsibleUser ?? null);
    setIsResponsibleDialogOpen(true);
  };

  const handleSaveResponsibleUser = async () => {
    if (!orderId) return;

    setIsSavingResponsibleUser(true);
    try {
      await updateOrder(orderId, { responsibleUserId: selectedResponsibleUser?.id ?? null });
      toast.success('Verantwoordelijke gewijzigd');
      setIsResponsibleDialogOpen(false);
      await refetchData();
    } catch (err) {
      toast.error(getDisplayError(err));
    } finally {
      setIsSavingResponsibleUser(false);
    }
  };

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  if (!order) {
    return (
      <ErrorState
        error={error}
        title={error ? 'Order kon niet worden geladen' : 'Order niet gevonden'}
        description={error ? getDisplayError(error) : 'De gevraagde order werd niet gevonden.'}
        onRetry={error ? refetchData : undefined}
        variant="page"
      />
    );
  } else {
    // Get lowest loadDate from all stops
    const executionDate = order.lines
      .map((l) => {
        return l.stops.filter((s) => s.type === OrderLineStopType.Load).sort((a, b) => sortDate(a.date, b.date))[0]
          ?.date;
      })
      .sort(sortDate)[0];
    const pageTitle = `Order ${order.orderNumber ?? 'DRAFT'}`;

    const profit = saleTotal - purchaseTotal;
    const profitPercentage = calculateProfitPercentage(purchaseTotal, saleTotal);

    const purchaseStatusColor = PURCHASE_STATUS_COLOR[order.purchaseStatus] ?? 'neutral';
    const purchaseStatusText =
      PURCHASE_STATUS_OPTIONS.find((i) => i.key === order.purchaseStatus)?.name ?? order.purchaseStatus;
    const saleStatusColor = SALE_STATUS_COLOR[order.saleStatus] ?? 'neutral';
    const saleStatusText = SALE_STATUS_OPTIONS.find((i) => i.key === order.saleStatus)?.name ?? order.saleStatus;

    const hasCmr = !!order.documents.find((d) => d.type === OrderDocumentType.Cmr)?.document;
    const originText = ORDER_ORIGINS[order.origin];
    // Driven km (planner-registered loaded + empty, with route/auto fallbacks) is the source of
    // truth for displayed distance and CO2 — same figures as the sustainability report.
    const km = getOrderKmBreakdown(order, order.lines);
    const distance = km.totalKm;
    // €/km is what the customer pays per km of their ride: empty km are our cost, not theirs.
    const euroPerKm = calculateEuroPerKm(saleTotal, km.fullKm);
    const emissions = getOrderSustainabilityFigures(order, order.lines).totalEmissionsGrams;
    const showDrivenBreakdown = km.emptyKm > 0 || order.manualFullKm != null || order.manualEmptyKm != null;
    return (
      <>
        <OrderProvider order={order} refreshData={refetchData}>
          <PageHeader title={pageTitle} />

          {showManualStatusChangeDialog && (
            <OrderStatusDialog
              orderId={order.id}
              initialStatus={order.status}
              isOpen={showManualStatusChangeDialog}
              setOpen={setShowManualStatusChangeDialog}
            />
          )}

          <PageHeading
            leftSide={
              <div className="flex items-center gap-2">
                <Breadcrumb
                  parentItem={{
                    name: 'Orders',
                    to: '/internal/orders',
                  }}
                  currentItem={pageTitle}
                />
                <div
                  className="ml-4 cursor-pointer"
                  onClick={() => {
                    setShowManualStatusChangeDialog(true);
                  }}
                >
                  <OrderStatusTag status={order.status as OrderStatus} />
                </div>
              </div>
            }
            rightSide={
              <div className="flex gap-2">
                {![OrderStatus.Executed, OrderStatus.Confirmed, OrderStatus.Cancelled].includes(
                  order.status as OrderStatus,
                ) &&
                  (!missingOrderData ? (
                    <ContactsAndContentDialog
                      triggerText="Bevestig order"
                      triggerColor={order.status === OrderStatus.Draft ? 'primary' : 'default'}
                      title="Verstuur order bevestiging"
                      submitText="Verstuur"
                      description="Ben je zeker dat de order volledig is?"
                      contacts={
                        order.customer?.contacts
                          .filter((c) => c.shouldReceiveOrderConfirmations)
                          .map((c) => {
                            return {
                              id: '' + c.id,
                              name: c.name,
                              email: c.email,
                            };
                          }) ?? []
                      }
                      initialContent={
                        minimalSettings.emailContents.find(
                          (v) => v.emailType === EmailType.OrderConfirmation && v.language === order.customer?.language,
                        )?.content ?? ''
                      }
                      initialValue={
                        order.customer?.contacts
                          .filter((c) => c.shouldReceiveOrderConfirmations)
                          .map((v) => '' + v.id) ?? []
                      }
                      extraInitialValues={{ prepayment: order.customer?.requiresPrepayment ?? false }}
                      extraFields={
                        order.status === OrderStatus.Draft && (
                          <CheckboxField labelText="Vooruitbetaling (factureren vóór uitvoering)" name="prepayment" />
                        )
                      }
                      onSubmit={async (values) => {
                        try {
                          await sendOrderConfirmationRequest(order.id, {
                            contentOverwrite: values.content,
                            contacts: values.contacts.map((c) => +c),
                            prepayment:
                              order.status === OrderStatus.Draft ? Boolean(values.extra.prepayment) : undefined,
                          });
                          trackEvent('order_confirmation_email_sent', {
                            order_id: order.id,
                            contact_count: values.contacts.length,
                            content_overwritten: Boolean(values.content),
                          });
                          toast.success('Order bevestiging verstuurd');
                          await refetchData();
                        } catch (err: any) {
                          captureException(err);
                          toast.error('Kon order bevestiging niet versturen: ' + getDisplayError(err));
                          throw err;
                        }
                      }}
                    />
                  ) : (
                    <Tooltip content={`Kan order niet bevestigen, ${missingOrderData}`}>
                      <Button isDisabled={true}>Bevestig Order</Button>
                    </Tooltip>
                  ))}

                {[OrderStatus.Created, OrderStatus.Placed, OrderStatus.Confirmed].includes(
                  order.status as OrderStatus,
                ) &&
                  order.customer &&
                  order.supplier &&
                  (!missingOrderData ? (
                    <ContactsAndContentDialog
                      triggerText="Verzend Transport Opdracht"
                      triggerColor={order.status === OrderStatus.Created ? 'primary' : 'default'}
                      title="Verzend transport opdracht"
                      submitText="Verzend"
                      description={`Ben je zeker dat je de transport opdracht naar ${order.supplier?.name} wilt verzenden?`}
                      contacts={order
                        .supplier!.contacts.filter((c) => c.shouldReceiveTransportOrders)
                        .map((c) => {
                          return {
                            id: '' + c.id,
                            name: c.name,
                            email: c.email,
                          };
                        })}
                      initialContent={
                        minimalSettings.emailContents.find(
                          (v) => v.emailType === EmailType.TransportOrder && v.language === order.supplier?.language,
                        )?.content ?? ''
                      }
                      initialValue={order
                        .supplier!.contacts.filter((c) => c.shouldReceiveTransportOrders)
                        .map((v) => '' + v.id)}
                      onSubmit={async (value) => {
                        try {
                          await sendTransportOrderRequest(order.id, {
                            contentOverwrite: value.content,
                            contacts: value.contacts.map((c) => +c),
                          });
                          trackEvent('order_transport_order_email_sent', {
                            order_id: order.id,
                            supplier_id: order.supplier?.id ?? null,
                            contact_count: value.contacts.length,
                            content_overwritten: Boolean(value.content),
                          });
                          toast.success(
                            'Transport order wordt op de achtergrond verstuurd. Je krijgt een melding als er iets misgaat.',
                          );
                          await refetchData();
                        } catch (err: any) {
                          captureException(err);
                          toast.error('Kon transport order niet versturen: ' + getDisplayError(err));
                          throw err;
                        }
                      }}
                    />
                  ) : (
                    <Tooltip content={`Kan transport opdracht niet verzenden, ${missingOrderData}`}>
                      <Button isDisabled={true}>Verzend Transport Opdracht</Button>
                    </Tooltip>
                  ))}

                {[OrderStatus.Executed].includes(order.status as OrderStatus) && order.customer && order.supplier && (
                  <ContactsDialog
                    triggerText="Verzend CMR Herinnering"
                    triggerColor={hasCmr ? 'default' : 'primary'}
                    title="Verzend CMR Herinnering"
                    submitText="Verzend"
                    description={`Ben je zeker dat je de transporteur ${order.supplier?.name} een CMR herinnering wilt sturen?`}
                    contacts={order
                      .supplier!.contacts.filter((c) => c.shouldReceiveTransportOrders)
                      .map((c) => {
                        return {
                          id: '' + c.id,
                          name: c.name,
                          email: c.email,
                        };
                      })}
                    initialValue={order
                      .supplier!.contacts.filter((c) => c.shouldReceiveTransportOrders)
                      .map((v) => '' + v.id)}
                    onSubmit={async (value) => {
                      try {
                        await sendUrgentCmrReminderRequest(order.id, {
                          contacts: value.map((c) => +c),
                        });
                        trackEvent('order_cmr_reminder_email_sent', {
                          order_id: order.id,
                          supplier_id: order.supplier?.id ?? null,
                          contact_count: value.length,
                        });
                        toast.success('CMR Herinnering verstuurd');
                        await refetchData();
                      } catch (err: any) {
                        captureException(err);
                        toast.error('Kon cmr herinnering niet versturen: ' + getDisplayError(err));
                        throw err;
                      }
                    }}
                  />
                )}

                <LinkButton to={`/internal/orders/new?copy-from=${order.id}`}>Dupliceer</LinkButton>

                {roleToNumber(me.role) >= roleToNumber(InternalRole.InternalEmployeePlus) && (
                  <OrderSelfBillExclusionAction order={order} onChange={refetchData} />
                )}

                {order.trailer && (
                  <LinkButton
                    to={`/internal/trailers/${order.trailer.id}/checks/new?orderId=${order.id}&orderNumber=${encodeURIComponent(order.orderNumber ?? '')}`}
                  >
                    Trailer check
                  </LinkButton>
                )}

                {order.status !== OrderStatus.Cancelled && order.saleStatus !== SaleStatus.Invoiced && (
                  <InputDialog
                    triggerText="Annuleer order"
                    triggerColor="danger"
                    title={`Annuleer order ${order.orderNumber ?? '-'}`}
                    submitText="Annuleer"
                    description={`Ben je zeker dat je order ${order.orderNumber ?? '-'} wilt annuleren?`}
                    labelText="Reden"
                    validation={Yup.string().min(5, 'Minimum 5 karakters')}
                    onSubmit={async (value: string) => {
                      try {
                        await cancelOrderRequest(order.id, value);
                        trackEvent('order_cancelled', {
                          order_id: order.id,
                          has_reason: Boolean(value),
                        });
                        toast.success('Order geannuleerd');
                        await refetchData();
                      } catch (err: any) {
                        captureException(err);
                        toast.error('Kon order niet annuleren: ' + getDisplayError(err));
                        throw err;
                      }
                    }}
                  />
                )}
              </div>
            }
          />

          <div className="px-4">
            <div className="flex justify-between gap-8 mb-4">
              <div className="flex-1 flex flex-wrap gap-6">
                <DataField title="Origine">
                  {order.originUrl ? (
                    <Link to={order.originUrl} className="link-text flex gap-1 items-center">
                      {originText} <ArrowSquareOutIcon className="w-4 h-4" />
                    </Link>
                  ) : (
                    originText
                  )}
                </DataField>
                {order.customer && (
                  <DataField title="Klant">
                    <span
                      className={classNames({
                        'text-negative-text': !!order.customer.deactivationReason,
                      })}
                    >{`${order.customer.name} - #${order.customer.id}${
                      order.customer.deactivationReason ? ' (gedeactiveerd)' : ''
                    }`}</span>
                  </DataField>
                )}
                {order.supplier && (
                  <DataField title="Vervoerder">
                    <span
                      className={classNames({
                        'text-negative-text': !!order.supplier.deactivationReason,
                      })}
                    >
                      {`${order.supplier.name} - #${order.supplier.id}${
                        order.supplier.deactivationReason ? ' (gedeactiveerd)' : ''
                      }`}
                    </span>
                  </DataField>
                )}
                {order.supplierTruck && <DataField title="Trekker">{order.supplierTruck.licensePlate}</DataField>}
                {order.trailer && (
                  <DataField title="Trailer">{order.trailer.name || order.trailer.licensePlate}</DataField>
                )}
                <DataField title="Referentie klant">{order.customerRef}</DataField>
                <DataField title="Ingave datum">{formatDate(order.createdAt)}</DataField>
                {distance > 0 && (
                  <DataField title="Afstand">
                    {showDrivenBreakdown
                      ? `${distance}km (gereden: ${km.fullKm} vol + ${km.emptyKm} leeg)`
                      : `${distance}km`}
                  </DataField>
                )}
                {emissions > 0 && (
                  <DataField title="Emissies">{`${decimateAndFormatNumber(emissions, 3, {
                    decimalSeperator: ',',
                  })}kg co2eq`}</DataField>
                )}
                {executionDate && <DataField title="Uitvoering datum">{formatDate(executionDate)}</DataField>}
                {order.cmrSentAt && <DataField title="CMR Verstuurd op">{formatDate(order.cmrSentAt)}</DataField>}
                <DataField title="Verantwoordelijke">
                  <div className="flex items-center gap-2">
                    <span>{order.responsibleUser?.name ?? '-'}</span>
                    <Button
                      color="ghost"
                      size={6}
                      onTrigger={handleOpenResponsibleDialog}
                      ariaLabel="Wijzig verantwoordelijke"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </DataField>
                {order.supplier && (
                  <DataField title="Toegewezen aan">
                    <div className="flex items-center gap-2">
                      <UserIcon className="w-4 h-4" />
                      <span>
                        {order.assignee
                          ? (order.supplier?.portalUsers?.find((u) => u.user.id === order.assignee?.id)?.nickname ??
                            order.assignee.name)
                          : '-'}
                      </span>
                      <Button color="ghost" onTrigger={handleOpenAssignDialog}>
                        Wijzig toewijzing
                      </Button>
                    </div>
                  </DataField>
                )}
                {order.supplier && <RatingDatafield order={order} onRated={refetchData} />}
                {order.cancellationReason && <DataField title="Annuleringsreden">{order.cancellationReason}</DataField>}
              </div>

              <div className="grid grid-cols-5 w-96 gap-x-4 gap-y-2 ml-4 self-start items-center card p-4">
                <div className="text-sm text-fg-secondary">Verkoop</div>
                <div className="text-right whitespace-nowrap col-span-2">{`${
                  saleTotal > 0
                    ? decimateAndFormatNumber(saleTotal, 2, {
                        decimalSeperator: ',',
                      })
                    : '-'
                } €`}</div>
                <div className="ml-auto col-span-2">
                  <Link
                    to={order.invoice ? `/internal/finance/sales/${order.invoice.id}` : '/internal/finance/sales/new'}
                  >
                    <Pill color={saleStatusColor} size="sm" className="text-center">
                      {saleStatusText}
                    </Pill>
                  </Link>
                </div>
                <div className="text-sm text-fg-secondary">Aankoop</div>
                <div className="text-right whitespace-nowrap col-span-2">{`${
                  purchaseTotal > 0
                    ? decimateAndFormatNumber(purchaseTotal, 2, {
                        decimalSeperator: ',',
                      })
                    : '-'
                } €`}</div>
                <div className="ml-auto col-span-2">
                  <Link
                    to={
                      order.purchase
                        ? `/internal/finance/purchases/${order.purchase.id}`
                        : '/internal/finance/purchases/new'
                    }
                  >
                    <Pill color={purchaseStatusColor} size="sm" className="text-center">
                      {purchaseStatusText}
                    </Pill>
                  </Link>
                </div>
                <div className="bg-border my-3 col-span-5 h-px"></div>
                <div className="text-sm text-fg-secondary">Documenten</div>
                <div className="ml-auto col-span-4">
                  <OrderDocumentValidationBadge
                    status={order.documentValidationStatus as OrderDocumentValidationStatus}
                  />
                </div>
                <div className="bg-border my-3 col-span-5 h-px"></div>
                <div className="text-sm text-fg-secondary font-semibold">Marge</div>
                <div className="text-right whitespace-nowrap col-span-2 font-semibold">{`${
                  profit
                    ? decimateAndFormatNumber(profit, 2, {
                        decimalSeperator: ',',
                      })
                    : '-'
                } €`}</div>
                <div className="text-right whitespace-nowrap col-span-2 font-semibold">
                  {`${
                    profitPercentage > 0
                      ? decimateAndFormatNumber(profitPercentage, 2, {
                          decimalSeperator: ',',
                        })
                      : '-'
                  }%`}
                </div>
                {euroPerKm !== null && (
                  <>
                    <div className="bg-border my-3 col-span-5 h-px"></div>
                    <div className="text-sm text-fg-secondary">€/km</div>
                    <div className="text-right whitespace-nowrap col-span-4">{`${decimateAndFormatNumber(euroPerKm, 2, {
                      decimalSeperator: ',',
                    })} €`}</div>
                  </>
                )}
              </div>
            </div>

            <OrderSelfBillReviewBanner
              orderId={order.id}
              flags={order.selfBillExcluded ? [] : ((order.selfBillReviewFlags ?? []) as OrderSelfBillReviewFlag[])}
              onChange={refetchData}
            />

            <OrderDocumentsBar order={order} />

            <Tabs
              items={tabItems}
              actions={
                canCreateOrderLine(order.status as OrderStatus) && (
                  <LinkButton to="lines/new">Nieuwe Opdracht</LinkButton>
                )
              }
            />
          </div>
        </OrderProvider>

        {/* Assignment Dialog */}
        <Dialog isOpen={isAssignDialogOpen} onClose={() => setIsAssignDialogOpen(false)} title="Opdracht toewijzen">
          <div className="space-y-4">
            <div>
              <label className="label-text block mb-2">Toegewezen aan</label>
              <SimpleSelect
                items={userOptions}
                selectedItem={userOptions.find((u) => u.key === selectedAssigneeId) ?? null}
                onSelect={(item) => setSelectedAssigneeId(item?.key ?? null)}
                isOptional={true}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button color="secondary" onTrigger={() => setIsAssignDialogOpen(false)} isDisabled={isAssigning}>
                Annuleer
              </Button>
              <Button color="primary" onTrigger={handleAssignOrder} isDisabled={isAssigning} isLoading={isAssigning}>
                Toewijzen
              </Button>
            </div>
          </div>
        </Dialog>

        {/* Responsible User Dialog */}
        <Dialog
          isOpen={isResponsibleDialogOpen}
          onClose={() => setIsResponsibleDialogOpen(false)}
          title="Verantwoordelijke wijzigen"
        >
          <div className="space-y-4">
            <div>
              <label className="label-text block mb-2">Verantwoordelijke</label>
              <UserCombobox value={selectedResponsibleUser} onChange={setSelectedResponsibleUser} />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                color="secondary"
                onTrigger={() => setIsResponsibleDialogOpen(false)}
                isDisabled={isSavingResponsibleUser}
              >
                Annuleer
              </Button>
              <Button
                color="primary"
                onTrigger={handleSaveResponsibleUser}
                isDisabled={isSavingResponsibleUser}
                isLoading={isSavingResponsibleUser}
              >
                Opslaan
              </Button>
            </div>
          </div>
        </Dialog>
      </>
    );
  }
};

export default OrderPage;
